import { Box, Divider, Grid, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { fMonthDayYearShort } from '../../../../../utils/formatTime';
import {
  getTransportContentSx,
  transportDetailsRowSx,
  transportDetailsSectionSx,
  transportFieldTitleSx,
  transportReadOnlySx,
} from '../transportTypographySx';
import { formatResidentList } from '../utils';

// Note: This view currently labels the section as "Resident Name", but we intentionally render
// request.residents as a list because the request shape still accounts for multiple residents for the case of group trip.
// We preserve this structure so it remains compatible when the group trip flow is reimplemented.

ResidentDetailsTab.propTypes = {
  request: PropTypes.object,
  residentDetails: PropTypes.object,
  residentDetailsError: PropTypes.string,
  editable: PropTypes.bool,
  onResidentFieldChange: PropTypes.func,
};

ResidentDetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editable: PropTypes.bool,
  field: PropTypes.string.isRequired,
  onResidentFieldChange: PropTypes.func,
};

const detailFieldSx = {
  display: 'inline-flex',
  minWidth: 54,
  width: 'fit-content',
  '& .MuiOutlinedInput-root': {
    width: 'fit-content',
    minWidth: 54,
    height: 30,
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#CFD4DB',
    },
    '&:hover fieldset': {
      borderColor: '#CFD4DB',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#CFD4DB',
      borderWidth: 1,
    },
    '&.Mui-disabled fieldset': {
      borderColor: '#CFD4DB',
    },
  },
  '& .MuiOutlinedInput-input': {
    py: 0.5,
    px: 1,
    fontSize: '14px',
    fieldSizing: 'content',
    width: 'auto',
    minWidth: '2ch',
  },
};

const locationFieldLabelSx = {
  ...transportFieldTitleSx,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  whiteSpace: 'nowrap',
};

function ResidentDetailField({ label, value, editable, field, onResidentFieldChange }) {
  return (
    <Typography component="div" sx={locationFieldLabelSx}>
      {label}:
      <TextField
        size="small"
        variant="outlined"
        value={value || ''}
        disabled={!editable}
        onChange={onResidentFieldChange?.(field)}
        sx={detailFieldSx}
      />
    </Typography>
  );
}

const formatGenderValue = (gender) => {
  if (typeof gender !== 'string') {
    return gender;
  }

  const normalizedGender = gender.trim().toLowerCase();

  if (normalizedGender === 'm') {
    return 'Male';
  }

  if (normalizedGender === 'f') {
    return 'Female';
  }

  return gender;
};

export default function ResidentDetailsTab({
  request,
  residentDetails,
  residentDetailsError,
  editable,
  onResidentFieldChange,
}) {
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ ...transportDetailsSectionSx, ...(!editable && transportReadOnlySx) }}>
          <Box sx={transportDetailsRowSx}>
            <Typography sx={transportFieldTitleSx}>
              Resident Name:
              <Box component="span" sx={{ ...getTransportContentSx(true), ml: '27px' }}>
                {formatResidentList(request?.residents)}
              </Box>
            </Typography>
          </Box>
          <Divider />
          {residentDetailsError ? (
            <Box sx={{ ...transportDetailsRowSx, py: 2 }}>
              <Typography variant="h6" color="error">
                {residentDetailsError}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ ...transportDetailsRowSx, alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                <ResidentDetailField
                  label="Unit"
                  value={residentDetails?.unit}
                  editable={editable}
                  field="unit"
                  onResidentFieldChange={onResidentFieldChange}
                />
                <ResidentDetailField
                  label="Room"
                  value={residentDetails?.room}
                  editable={editable}
                  field="room"
                  onResidentFieldChange={onResidentFieldChange}
                />
                <ResidentDetailField
                  label="Bed"
                  value={residentDetails?.bed}
                  editable={editable}
                  field="bed"
                  onResidentFieldChange={onResidentFieldChange}
                />
              </Box>
              <Divider />
              {/* <Box sx={{ ...transportDetailsRowSx, alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            <Typography sx={locationFieldLabelSx}>
              Movement Level:
             <Autocomplete
                size="small"
                sx={{ minWidth: 170 }}
                // value={doctorsList.find((d) => d.value === formState[doctorIdField]) ?? null}
                // onChange={(_, newValue) => setFields(doctorIdField, newValue?.value ?? '')}
                // options={isLoading ? [] : doctorsList}
                // getOptionLabel={(option) => option?.label || ''}
                // isOptionEqualToValue={(option, value) => option?.value === value?.value}
                // renderInput={(params) => (
                //   <TextField
                //     {...params}
                //     label={autocompleteDisabled ? '' : 'Select a doctor'}
                //     autoComplete="off"
                //     disabled={autocompleteDisabled}
                //   />
                // )}
                // loading={isLoading}
              />
            </Typography>
          </Box>
          <Divider /> */}
              <Box sx={{ ...transportDetailsRowSx, alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <Typography sx={transportFieldTitleSx}>
                  DOB:
                  <Box component="span" sx={{ ...getTransportContentSx(true), ml: '25px' }}>
                    {fMonthDayYearShort(residentDetails?.dob)}
                  </Box>
                </Typography>
                <Typography sx={transportFieldTitleSx}>
                  Gender:
                  <Box component="span" sx={{ ...getTransportContentSx(true), ml: '25px' }}>
                    {formatGenderValue(residentDetails?.gender)}
                  </Box>
                </Typography>
                <Typography sx={transportFieldTitleSx}>
                  Weight:
                  <Box component="span" sx={{ ...getTransportContentSx(true), ml: '25px' }}>
                    {residentDetails?.weight}
                  </Box>
                </Typography>
              </Box>
              <Divider />
            </>
          )}
          {/* <Box sx={transportDetailsRowSx}>
            <Box>
              <Typography sx={transportFieldTitleSx}>Insurance Details:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mt: '17px' }}>
                <Typography sx={transportFieldTitleSx}>
                  Name:
                  <Box component="span" sx={{ ...getTransportContentSx(true), ml: '25px' }}>
                    Insurance name placeholder
                  </Box>
                </Typography>
                <Typography sx={transportFieldTitleSx}>
                  Number:
                  <Box component="span" sx={{ ...getTransportContentSx(true), ml: '25px' }}>
                    1234567890
                  </Box>
                </Typography>
                <Typography sx={transportFieldTitleSx}>
                  Kind:
                  <Box component="span" sx={{ ...getTransportContentSx(true), ml: '25px' }}>
                    Insurance kind placeholder
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider /> */}
        </Grid>
      </Grid>
    </>
  );
}
